export type LineStyle =
  | 'header'
  | 'data'
  | 'narration'
  | 'alert'
  | 'success'
  | 'warning'
  | 'dim'
  | 'accent'
  | 'blank'

export interface StoryLine {
  text: string
  style: LineStyle
}

export interface StoryChoice {
  label: string
  tag: string
  next: string
}

export interface StoryNode {
  id: string
  lines: StoryLine[]
  choices?: StoryChoice[]
}

export const STORY_START = 'start'

export const STORY: Record<string, StoryNode> = {
  start: {
    id: 'start',
    lines: [
      { text: '── SQUALL INTERCEPT · 41°18N 63°42W ──', style: 'header' },
      { text: '', style: 'blank' },
      { text: 'TWS 18 kt · TWD 228° · boat speed 11.4 kt', style: 'data' },
      { text: 'Pressure has fallen 2.8 hPa in 90 minutes.', style: 'warning' },
      { text: 'Radar shows a fast cell crossing the route from west.', style: 'alert' },
      { text: '', style: 'blank' },
      { text: 'The model route still passes through the cell edge.', style: 'narration' },
    ],
    choices: [
      { label: 'Hold the route and watch the cell', tag: 'FAST / EXPOSED', next: 'hold' },
      { label: 'Bear away early and pass behind it', tag: 'SAFE / LONGER', next: 'avoid' },
      { label: 'Slow down and let the cell cross ahead', tag: 'CONTROLLED', next: 'slow' },
    ],
  },
  hold: {
    id: 'hold',
    lines: [
      { text: '', style: 'blank' },
      { text: 'You hold course for eight minutes.', style: 'narration' },
      { text: 'TWD shifts 31° and gusts rise to 34 kt.', style: 'alert' },
      { text: 'The original route is now inside the strongest return.', style: 'warning' },
    ],
    choices: [
      { label: 'Douse now and turn behind the cell', tag: 'RECOVER', next: 'recover' },
      { label: 'Carry on through the edge', tag: 'HIGH RISK', next: 'impact' },
    ],
  },
  avoid: {
    id: 'avoid',
    lines: [
      { text: '', style: 'blank' },
      { text: 'You alter 22° before the gust front arrives.', style: 'success' },
      { text: 'The cell crosses 5 nm ahead. Peak gust aboard: 24 kt.', style: 'data' },
      { text: 'Route cost: 11 minutes. Handling cost: low.', style: 'accent' },
      { text: '', style: 'blank' },
      { text: 'Vortex learns: early pressure trend beat the deterministic route.', style: 'success' },
    ],
  },
  slow: {
    id: 'slow',
    lines: [
      { text: '', style: 'blank' },
      { text: 'You reduce sail and slow the intercept.', style: 'narration' },
      { text: 'The cell passes 2 nm ahead, but the trailing wind is unstable.', style: 'warning' },
    ],
    choices: [
      { label: 'Follow the clearing behind the cell', tag: 'PRESSURE', next: 'recover' },
      { label: 'Return directly to the old route', tag: 'SHORTEST', next: 'wake' },
    ],
  },
  recover: {
    id: 'recover',
    lines: [
      { text: '', style: 'blank' },
      { text: 'The douse is complete before the strongest gust.', style: 'success' },
      { text: 'You cross the wake in flatter water and rebuild speed.', style: 'data' },
      { text: 'Route cost: 18 minutes. Boat and crew remain at full capacity.', style: 'accent' },
      { text: '', style: 'blank' },
      { text: 'Best decision: preserve options before the forecast becomes certainty.', style: 'success' },
    ],
  },
  wake: {
    id: 'wake',
    lines: [
      { text: '', style: 'blank' },
      { text: 'The shortest line enters the cell wake.', style: 'narration' },
      { text: 'Wind falls to 7 kt and rotates through 50°.', style: 'warning' },
      { text: 'The safer-looking distance costs 27 minutes in lost pressure.', style: 'alert' },
      { text: '', style: 'blank' },
      { text: 'Lesson: distance is not speed when the weather field is moving.', style: 'accent' },
    ],
  },
  impact: {
    id: 'impact',
    lines: [
      { text: '', style: 'blank' },
      { text: 'Peak gust: 39 kt. The boat broaches during the late douse.', style: 'alert' },
      { text: 'No injury, but the recovery costs 42 minutes.', style: 'warning' },
      { text: '', style: 'blank' },
      { text: 'Lesson: the fastest route stopped being fastest before the crew acted.', style: 'accent' },
    ],
  },
}
